import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import { Users, Loader2, Building2, Shield, CheckCircle, AlertCircle } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import AppFooter from '../components/AppFooter';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const ROLE_LABELS = {
  viewer: 'Viewer - Can view all data but cannot make changes',
  editor: 'Editor - Can add, edit, and delete transactions',
  admin: 'Admin - Full access including team management'
};

export default function JoinTeamPage() {
  const { token } = useParams();
  const navigate = useNavigate();
  const { isAuthenticated, user } = useAuth();
  
  const [invite, setInvite] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [joining, setJoining] = useState(false);
  const [joined, setJoined] = useState(false);
  
  // Registration form
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: '',
    confirmPassword: ''
  });
  const [registering, setRegistering] = useState(false);

  useEffect(() => {
    fetchInviteDetails();
  }, [token]);

  const fetchInviteDetails = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/team/invite/${token}`);
      setInvite(response.data);
      setFormData(prev => ({ ...prev, email: response.data.email }));
    } catch (err) {
      setError(err.response?.data?.detail || 'Invalid or expired invitation link');
    } finally {
      setLoading(false);
    }
  };

  const handleJoinAsExistingUser = async () => {
    setJoining(true);
    try {
      await axios.post(`${API}/team/join/${token}`);
      setJoined(true);
      toast.success(`You have joined ${invite.business_name}!`);
      setTimeout(() => navigate('/dashboard'), 2000);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to join team');
    } finally {
      setJoining(false);
    }
  };

  const passwordIssue = (() => {
    const pw = formData.password || '';
    if (!pw) return null;
    if (pw.length < 8) return 'Must be at least 8 characters';
    if (!/[A-Za-z]/.test(pw)) return 'Must contain a letter';
    if (!/\d/.test(pw)) return 'Must contain a number';
    if (!/[^A-Za-z0-9]/.test(pw)) return 'Must contain a symbol (e.g. !@#$%)';
    return null;
  })();

  const handleRegisterAndJoin = async (e) => {
    e.preventDefault();

    if (!formData.name.trim()) {
      toast.error('Please enter your name');
      return;
    }
    if (passwordIssue) {
      toast.error(`Password too weak: ${passwordIssue}`);
      return;
    }
    if (formData.password !== formData.confirmPassword) {
      toast.error('Passwords do not match');
      return;
    }

    setRegistering(true);
    try {
      // First register the user — server may reject if account already exists
      let accountExisted = false;
      try {
        await axios.post(`${API}/auth/register`, {
          email: formData.email,
          password: formData.password,
          name: formData.name,
          // Backend requires business_name (creates a personal default business).
          // After accept-invite, active_business_id is auto-switched to the
          // inviter's business, so this default biz is just a placeholder.
          business_name: `${formData.name}'s workspace`,
          accepted_terms: true,
        });
      } catch (regErr) {
        const status = regErr.response?.status;
        const detail = regErr.response?.data?.detail || '';
        const alreadyExists =
          status === 400 && /already|exists|registered/i.test(detail);
        if (!alreadyExists) {
          throw regErr;
        }
        accountExisted = true;
      }

      // Login with the credentials
      let loginResponse;
      try {
        loginResponse = await axios.post(`${API}/auth/login`, {
          email: formData.email,
          password: formData.password,
        });
      } catch (loginErr) {
        // If account already existed AND login failed, the password they typed
        // does NOT match the existing account. Direct them to /login or password-reset.
        if (accountExisted) {
          toast.error(
            `An account for ${formData.email} already exists with a different password. Please log in with your existing password, then accept this invite again.`,
            { duration: 8000 },
          );
          setTimeout(() => {
            window.location.href = `/login?next=/join/${token}`;
          }, 3000);
          return;
        }
        throw loginErr;
      }

      const authToken = loginResponse.data.access_token;
      axios.defaults.headers.common['Authorization'] = `Bearer ${authToken}`;
      localStorage.setItem('token', authToken);
      // Flag that the IP notice should appear once on the next AuthContext init
      // (which happens after the hard-reload below).
      localStorage.setItem('cfp_show_ip_notice', '1');

      // Join the team
      await axios.post(`${API}/team/join/${token}`);

      setJoined(true);
      toast.success(`Account ready — welcome to ${invite.business_name}!`);

      // Hard reload to /dashboard so AuthContext re-initialises with the new token
      setTimeout(() => {
        window.location.href = '/dashboard';
      }, 2500);
    } catch (err) {
      const detail = err.response?.data?.detail;
      let errorMsg = err.message || 'Registration failed';
      if (typeof detail === 'string') {
        errorMsg = detail;
      } else if (Array.isArray(detail)) {
        // FastAPI 422 returns [{loc, msg, type, ...}] — surface the first msg
        errorMsg = detail.map((e) => (e?.msg ? `${e.msg}` : '')).filter(Boolean).join(' · ') || errorMsg;
      }
      toast.error(errorMsg);
    } finally {
      setRegistering(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex flex-col bg-gradient-to-br from-blue-600 to-blue-800">
        <div className="flex-1 flex items-center justify-center">
          <Loader2 className="w-8 h-8 animate-spin text-white" />
        </div>
        <AppFooter />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex flex-col bg-gradient-to-br from-blue-600 to-blue-800">
        <div className="flex-1 flex items-center justify-center p-4">
          <Card className="w-full max-w-md">
            <CardContent className="pt-6 text-center">
              <AlertCircle className="w-12 h-12 mx-auto mb-4 text-red-500" />
              <h2 className="text-xl font-bold text-slate-900 mb-2">Invalid Invitation</h2>
              <p className="text-slate-500 mb-6">{error}</p>
              <Link to="/login">
                <Button>Go to Login</Button>
              </Link>
            </CardContent>
          </Card>
        </div>
        <AppFooter />
      </div>
    );
  }

  if (joined) {
    return (
      <div className="min-h-screen flex flex-col bg-gradient-to-br from-blue-600 to-blue-800">
        <div className="flex-1 flex items-center justify-center p-4">
          <Card className="w-full max-w-md">
            <CardContent className="pt-6 text-center space-y-3">
              <CheckCircle className="w-12 h-12 mx-auto text-green-500" />
              <h2 className="text-xl font-bold text-slate-900">Welcome to the Team!</h2>
              <p className="text-slate-500">
                You have successfully joined <strong>{invite.business_name}</strong>
              </p>
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 text-left text-sm">
                <p className="text-slate-600 mb-1">
                  <strong>Important — your login email is:</strong>
                </p>
                <p className="font-mono font-semibold text-blue-700 break-all">
                  {invite.email}
                </p>
                <p className="text-xs text-slate-500 mt-2">
                  Use this email (not the inviter's) the next time you log in.
                </p>
              </div>
              <p className="text-sm text-slate-400">Taking you to the dashboard…</p>
            </CardContent>
          </Card>
        </div>
        <AppFooter />
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col bg-gradient-to-br from-blue-600 to-blue-800">
      <div className="flex-1 flex items-center justify-center p-4">
      <Card className="w-full max-w-md">
        <CardHeader className="text-center">
          <div className="mx-auto mb-4 p-3 bg-blue-100 rounded-full w-fit">
            <Users className="w-8 h-8 text-blue-600" />
          </div>
          <CardTitle className="text-xl">Team Invitation</CardTitle>
          <CardDescription>
            You've been invited to join a team
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Invitation Details */}
          <div className="bg-slate-50 dark:bg-slate-800 rounded-lg p-4 space-y-3">
            <div className="flex items-center gap-3">
              <Building2 className="w-5 h-5 text-slate-400" />
              <div>
                <p className="text-xs text-slate-500">Business</p>
                <p className="font-semibold">{invite.business_name}</p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <Shield className="w-5 h-5 text-slate-400" />
              <div>
                <p className="text-xs text-slate-500">Your Role</p>
                <p className="font-medium capitalize">{invite.role}</p>
                <p className="text-xs text-slate-400">{ROLE_LABELS[invite.role]}</p>
              </div>
            </div>
          </div>

          {/* If user is already logged in */}
          {isAuthenticated ? (
            <div className="space-y-4">
              <p className="text-sm text-slate-600 text-center">
                You're logged in as <strong>{user?.email}</strong>
              </p>
              <Button 
                onClick={handleJoinAsExistingUser} 
                disabled={joining}
                className="w-full gap-2"
              >
                {joining ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle className="w-4 h-4" />}
                Join {invite.business_name}
              </Button>
              <p className="text-xs text-slate-400 text-center">
                Not you? <Link to="/login" className="text-blue-600 hover:underline">Login with a different account</Link>
              </p>
            </div>
          ) : (
            /* Registration form for new users */
            <form onSubmit={handleRegisterAndJoin} className="space-y-4">
              <p className="text-sm text-slate-600 text-center">
                Create an account to join the team
              </p>
              
              <div className="space-y-2">
                <Label>Your Name</Label>
                <Input
                  type="text"
                  name="full-name"
                  placeholder="John Smith"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  required
                  autoComplete="off"
                  data-testid="join-name-input"
                />
                <p className="text-xs text-slate-400">Your own name — not the person who invited you.</p>
              </div>

              <div className="space-y-2">
                <Label>Email</Label>
                <Input
                  type="email"
                  value={formData.email}
                  disabled
                  className="bg-slate-100"
                  autoComplete="off"
                  data-testid="join-email-input"
                />
                <p className="text-xs text-slate-400">
                  This is the email you'll use to log in next time — make sure it's yours.
                </p>
              </div>

              <div className="space-y-2">
                <Label>Password</Label>
                <Input
                  type="password"
                  placeholder="Min 8 chars · letter · number · symbol"
                  value={formData.password}
                  onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                  required
                  minLength={8}
                  autoComplete="new-password"
                  data-testid="join-password-input"
                />
                {formData.password && (
                  <p className={`text-xs ${passwordIssue ? 'text-amber-600' : 'text-emerald-600'}`}>
                    {passwordIssue ? `· ${passwordIssue}` : '✓ Strong password'}
                  </p>
                )}
              </div>

              <div className="space-y-2">
                <Label>Confirm Password</Label>
                <Input
                  type="password"
                  placeholder="Confirm your password"
                  value={formData.confirmPassword}
                  onChange={(e) => setFormData({ ...formData, confirmPassword: e.target.value })}
                  required
                  autoComplete="new-password"
                  data-testid="join-confirm-password-input"
                />
              </div>

              <Button 
                type="submit"
                disabled={registering}
                className="w-full gap-2"
                data-testid="join-submit-btn"
              >
                {registering ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle className="w-4 h-4" />}
                Create Account & Join Team
              </Button>

              <p className="text-xs text-slate-400 text-center">
                Already have an account? <Link to="/login" className="text-blue-600 hover:underline">Login first</Link>
              </p>
            </form>
          )}
        </CardContent>
      </Card>
      </div>
      <AppFooter />
    </div>
  );
}
